import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  User,
  LoginRequest,
  MeResponse,
  DashboardScopeInfo,
} from '@/types/api';
import { apiClient } from '@/services/api';
import { websocketService } from '@/services/websocket';
import { queryClient } from '@/services/queryClient';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  permissions: Set<string>;
  scope: DashboardScopeInfo | null;
  // True while the boot-time /me hydrate is in flight for a persisted session with no
  // cached permissions yet, so ProtectedRoute can hold off rendering instead of flashing
  // the dashboard with stale/empty permissions (M1 fix).
  isHydrating: boolean;

  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
  setSession: (me: MeResponse) => void;
}

// Computed synchronously at module load, from the same localStorage key the persist
// middleware reads, so the very first render already reflects whether a boot-time
// hydrate is about to run - avoiding a one-frame flash before the App effect fires.
function computeInitialHydrating(): boolean {
  try {
    const raw = localStorage.getItem('auth-storage');
    if (!raw) return false;
    const persistedState = JSON.parse(raw)?.state;
    const permissions = persistedState?.permissions;
    return Boolean(persistedState?.isAuthenticated) && (!Array.isArray(permissions) || permissions.length === 0);
  } catch {
    return false;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      permissions: new Set<string>(),
      scope: null,
      isHydrating: computeInitialHydrating(),

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });

        // Wipe any cached query data from a previous identity in this tab
        // (e.g. a prior user's role/user list) before the new session's
        // pages can render off of it -- react-query keeps a query's last
        // successful `data` around even after a later fetch errors, so a
        // permission-denied refetch under the new identity would otherwise
        // silently keep showing the old user's cached response.
        queryClient.clear();

        try {
          const response = await apiClient.login(credentials);

          // Store tokens
          localStorage.setItem('accessToken', response.token);
          localStorage.setItem('refreshToken', response.refreshToken);

          set({
            user: response.user,
            accessToken: response.token,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Hydrate permissions and scope from /me. Login response only carries the legacy User payload;
          // permissions+scope are a separate session concern owned by /api/v1/me. Without them the user
          // can't use the dashboard (RequirePermission redirects to /forbidden with no way back), so a
          // /me failure here fails the whole login instead of leaving a dead-end empty-permissions session.
          const me = await apiClient.getMe();
          get().setSession(me);

          // Connect WebSocket after successful login
          try {
            await websocketService.connect();
          } catch {
            // Don't fail login if WebSocket fails
          }
        } catch (error: any) {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            'Login failed';

          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');

          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });

          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });

        try {
          // Disconnect WebSocket
          websocketService.disconnect();

          // Call logout endpoint
          await apiClient.logout();
        } catch {
          // Continue with logout even if API call fails
        } finally {
          // Clear local state
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');

          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });

          // Same reasoning as login()'s clear -- don't leave this user's
          // cached data sitting in memory for whoever logs in next.
          queryClient.clear();
        }
      },

      refreshUser: async () => {
        if (!get().isAuthenticated) {
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const me = await apiClient.getMe();
          get().setSession(me);

          set({
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            'Failed to refresh user';

          set({
            isLoading: false,
            error: errorMessage,
          });

          // If refresh fails with 401, logout
          if (error.response?.status === 401) {
            get().logout();
          }
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setUser: (user: User) => {
        set({ user });
      },

      setSession: (me: MeResponse) => {
        set((state) => {
          const base: User =
            state.user ?? { id: 0, username: '', email: '', active: true };
          const mergedUser: User = {
            ...base,
            id: me.user.id,
            username: me.user.username,
            email: me.user.email,
            firstName: me.user.firstName,
            lastName: me.user.lastName,
          };
          return {
            user: mergedUser,
            permissions: new Set<string>(me.permissions),
            scope: me.scope,
          };
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        permissions: Array.from(state.permissions),
        scope: state.scope,
      }),
      merge: (persisted: any, current) => ({
        ...current,
        ...(persisted ?? {}),
        permissions: new Set<string>(persisted?.permissions ?? []),
      }),
    }
  )
);
